<cs-header>
  <cs-header-title>{{ LL.coach.title() }}</cs-header-title>
  @if (canUseBranding()) {
    <cs-icon-button header-right icon="magic-wand"
      [ariaLabel]="LL.coach.branding.headerAffordance()"
      (click)="openBranding()"></cs-icon-button>
  }
  <cs-icon-button header-right icon="credit-card"
    [ariaLabel]="LL.coach.billing.headerAffordance()"
    (click)="openBilling()"></cs-icon-button>
  <cs-icon-button header-right icon="refresh"
    [ariaLabel]="LL.coach.dashboard.refresh()"
    (click)="refreshDashboardTapped()"></cs-icon-button>
</cs-header>

<cs-content>
  @if (queuedPauseNotice; as notice) {
    <cs-note light>
      <strong>{{ LL.coach.tier.pauseScheduledNotice.title() }}</strong>
      {{ queuedPauseNoticeText(notice) }}
    </cs-note>
  }
  @if (clientSlots.length === 0) {
    <cs-empty-state
      iconName="people"
      [title]="LL.coach.emptyState.title()"
      [subtext]="slotsRemainingText()">
      <cs-button secondary small icon="add" (click)="addClient()">
        {{ LL.coach.addClient() }}
      </cs-button>
    </cs-empty-state>
  } @else {
    @if (claimedSlots.length > 0) {
      <cs-card-list class="dashboard-list">
        @for (slot of claimedSlots; track slot._id) {
          <cs-card hoverable tappable (click)="openClientDetail(slot)" [class.client-asleep]="isSlotAsleep(slot)">
            <div class="dashboard-row">
              <div class="dashboard-cell cell-client">
                <span class="client-nickname">
                  <!-- #4530: a parked client shows no presence dot. They are not
                       being coached, so "online now" is not a useful thing to
                       say about them, and the dot competes with the marker. -->
                  @if (hasPresenceData(slot) && !isSlotAsleep(slot)) {
                    <span class="presence-dot" [class.online]="isClientOnline(slot)"></span>
                  }
                  {{ slot.nickname || LL.coach.unnamedClient() }}
                </span>
                @if (isSlotAsleep(slot)) {
                  <span class="client-asleep-note">
                    <cs-icon name="moon"></cs-icon>
                    <span>{{ asleepForText(slot) }}</span>
                  </span>
                }
              </div>

              <div class="dashboard-cell cell-status">
                <!-- #4530: a parked client's on-track / off-track status is not
                     shown. It is computed from a plan they are not currently
                     being coached on, so it would be answering a question
                     nobody asked and would read as if coaching were ongoing. -->
                @if (isSlotAsleep(slot)) {
                  <cs-badge [label]="LL.coach.clientAsleep.badge()" [color]="asleepBadgeColor"></cs-badge>
                } @else if (hasBrokenHandover(slot)) {
                  <!-- #4817: this REPLACES the on-track badge rather than sitting
                       beside it. That status is computed from whatever program the
                       client's account is actually running, and the whole point of
                       this warning is that it is not the one the coach set up — so
                       showing it here would answer a question about the wrong
                       program while looking entirely healthy. -->
                  <cs-badge
                    [label]="LL.coach.dashboard.handoverIncompleteBadge()"
                    [color]="brokenHandoverBadgeColor"></cs-badge>
                } @else if (isDashboardLoading(slot)) {
                  <cs-skeleton width="80px"></cs-skeleton>
                } @else {
                  <cs-badge
                    [label]="getDashboardStatusLabel(getDashboardRow(slot))"
                    [color]="getDashboardStatusColor(getDashboardRow(slot))"></cs-badge>
                }
              </div>

              <div class="dashboard-cell cell-items">
                @if (isDashboardLoading(slot)) {
                  <cs-skeleton width="40px"></cs-skeleton>
                } @else {
                  <span class="items-count" [class.has-items]="(getDashboardRow(slot)?.items?.length || 0) > 0">
                    {{ getItemsCellLabel(getDashboardRow(slot)) }}
                  </span>
                }
              </div>
            </div>

            @if ((hasPresenceData(slot) && !isSlotAsleep(slot)) || showsTrainingOverview(slot)) {
              <div class="card-footer-row">
                <!-- hidden for a parked client, for the same reason the dot above
                     is: "last seen 2 hours ago" is not a fact about coaching that
                     is currently happening. -->
                @if (hasPresenceData(slot) && !isSlotAsleep(slot)) {
                  <div class="presence-label">{{ getPresenceLabel(slot) }}</div>
                }
                <!-- #6795: opens this client's Training Overview inside their account in one tap -->
                @if (showsTrainingOverview(slot)) {
                  <cs-button tertiary small class="open-training-overview" icon="trending-up"
                    [label]="LL.trainingOverview.title()" (click)="openTrainingOverview($event, slot)"></cs-button>
                }
              </div>
            }
          </cs-card>
        }
      </cs-card-list>
    }

    @if (pendingSlots.length > 0) {
      <div class="pending-section-title">{{ LL.coach.dashboard.pendingSectionTitle() }}</div>
      <cs-card-list class="pending-list">
        @for (slot of pendingSlots; track slot._id) {
          <cs-card hoverable tappable (click)="openClientDetail(slot)">
            <cs-card-title>
              <span class="client-nickname">
                {{ slot.nickname || LL.coach.unnamedClient() }}
              </span>
              <cs-badge title-action [label]="getStatusLabel(slot)" [color]="getStatusColor(slot)"></cs-badge>
            </cs-card-title>
          </cs-card>
        }
      </cs-card-list>
    }

    <cs-card-list>
      <cs-card-list-row class="add-client-footer">
        @if (!slotsAtCapacity) {
          <cs-button tertiary icon="add" (click)="addClient()">
            {{ LL.coach.addClient() }}
          </cs-button>
        }
        <span class="slots-info">{{ slotsRemainingText() }}</span>
      </cs-card-list-row>
    </cs-card-list>
  }
</cs-content>
